/* eslint-disable no-console */
import { convertirAMonedaLocal, loadExchangeRates } from "./exchangeRateService";
import { client } from "./supabase";

/**
 * Calcula el valor en libros (Net Book Value) de todos los activos para cada mes del año.
 * Para cada mes: Σ (costo_total - depreciación_acumulada) de activos comprados hasta ese mes.
 *
 * @param {string} pais - "Chile" o "Perú"
 * @param {number} year - Año para calcular
 * @returns {Promise<number[]>} Array de 12 elementos con valor en libros al cierre de cada mes (EN MONEDA LOCAL)
 */
export const getValorActivosMensual = async (pais, year) => {
  const yearNum = Number(year);
  try {
    await loadExchangeRates();

    const { data: activos, error } = await client
      .from("activos")
      .select("fecha_compra, costo_unitario, cantidad, moneda, vida_util_meses, estado")
      .eq("pais", pais)
      .eq("eliminado", false);

    if (error) throw error;
    if (!activos?.length) return Array(12).fill(0);

    const valorPorMes = Array(12).fill(0);

    for (const activo of activos) {
      // Solo activos en uso contribuyen al valor en libros
      if (activo.estado !== "activo" && activo.estado !== "en_reparacion") continue;

      const [yearCompra, monthCompra] = activo.fecha_compra.split("-").map(Number);
      const costoTotal = parseFloat(activo.costo_unitario) * activo.cantidad;
      const vidaUtilMeses = activo.vida_util_meses || 60;

      // Convertir a moneda local
      const fechaStr = `${yearCompra}-${String(monthCompra).padStart(2, "0")}-15`;
      const costoLocalTotal = convertirAMonedaLocal(costoTotal, activo.moneda, pais, fechaStr);
      const cuotaLocal = costoLocalTotal / vidaUtilMeses;

      for (let mes = 0; mes < 12; mes++) {
        // Fin de mes evaluado
        const finMes = new Date(yearNum, mes + 1, 0);
        const fechaCompra = new Date(yearCompra, monthCompra - 1, 1);

        // Si no se compró aún, no contribuye
        if (fechaCompra > finMes) continue;

        // Meses transcurridos desde la compra hasta este mes (inclusive)
        const mesesDesdeCompra = (yearNum - yearCompra) * 12 + mes - (monthCompra - 1) + 1;

        // Depreciación acumulada (sin exceder vida útil)
        const mesesDepreciados = Math.min(mesesDesdeCompra, vidaUtilMeses);
        const depAcumulada = cuotaLocal * mesesDepreciados;

        // Valor en libros = costo - depreciación acumulada (mínimo 0)
        const valorLibros = Math.max(0, costoLocalTotal - depAcumulada);

        valorPorMes[mes] += Math.round(valorLibros);
      }
    }

    return valorPorMes;
  } catch (error) {
    console.error("Error en getValorActivosMensual:", error);
    return Array(12).fill(0);
  }
};
